import { BookOpen, X as CloseIcon, Container, Loader2, Terminal } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { skillService } from "@/lib/services";
import type { SkillSummaryForExtension } from "@/types";
import { ColorPicker, IconPicker } from "./IconColorPickers";

const SKILL_TYPE_OPTIONS = [
  {
    value: "context" as const,
    label: "Context",
    description: "Instructions only — guides the AI without code execution",
    icon: BookOpen,
  },
  {
    value: "dynamic_session" as const,
    label: "Dynamic Session",
    description: "Run Python code in a sandboxed Azure Hyper-V session",
    icon: Terminal,
  },
  {
    value: "custom_container" as const,
    label: "Custom Container",
    description: "Execute in a dedicated Docker container with custom tools",
    icon: Container,
  },
];

export interface EditSkillModalProps {
  skill: SkillSummaryForExtension;
  onClose: () => void;
  onSave: () => void;
}

export function EditSkillModal({ skill, onClose, onSave }: Readonly<EditSkillModalProps>) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description);
  const [skillType, setSkillType] = useState(skill.skillType);
  const [instructions, setInstructions] = useState("");
  const [icon, setIcon] = useState(skill.icon);
  const [color, setColor] = useState(skill.color);
  const [category, setCategory] = useState(skill.category ?? "");
  const [tags, setTags] = useState(skill.tags.join(", "));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSkill = async () => {
      try {
        const response = await skillService.get(skill.uuid);
        const fullSkill = response.data;
        setInstructions(fullSkill.instructions ?? "");
      } catch (err) {
        console.error("Failed to load skill:", err);
        showToast("Failed to load skill details", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchSkill();
  }, [skill.uuid, showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !instructions.trim()) {
      showToast("Please fill in all required fields", "warning");
      return;
    }

    try {
      setSaving(true);
      const parsedTags = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      await skillService.update(skill.uuid, {
        name: name.trim(),
        description: description.trim() || undefined,
        skillType: skillType as "context" | "dynamic_session" | "custom_container",
        instructions: instructions.trim(),
        icon: icon.trim() || undefined,
        color: color.trim() || undefined,
        category: category.trim() || undefined,
        tags: parsedTags.length > 0 ? parsedTags : undefined,
      });
      showToast("Skill updated successfully", "success");
      onSave();
    } catch (err: unknown) {
      console.error("Failed to update skill:", err);
      showToast(getApiErrorMessage(err, "Failed to update skill"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content-primary">Edit Skill</h2>
          <button onClick={onClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label htmlFor="edit-skill-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                id="edit-skill-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Custom Skill"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            <div>
              <label
                htmlFor="edit-skill-description"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Description
              </label>
              <textarea
                id="edit-skill-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this skill do?"
                rows={2}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
              />
            </div>

            <div>
              <span className="block text-sm font-medium text-content-secondary mb-1.5">Execution Context</span>
              <div className="flex gap-3">
                {SKILL_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSkillType(opt.value)}
                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors text-center ${
                      skillType === opt.value
                        ? "border-brand-primary bg-brand-primary/5"
                        : "border-border-secondary bg-surface-secondary hover:border-content-quaternary"
                    }`}
                  >
                    <opt.icon
                      className={`w-6 h-6 ${skillType === opt.value ? "text-brand-primary" : "text-content-tertiary"}`}
                    />
                    <div className="font-medium text-sm text-content-primary">{opt.label}</div>
                    <p className="text-xs text-content-tertiary">{opt.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="edit-skill-instructions"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Instructions <span className="text-red-400">*</span>
              </label>
              <textarea
                id="edit-skill-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Enter the skill instructions..."
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none min-h-[200px]"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <IconPicker value={icon} onChange={setIcon} label="Icon" />
              <ColorPicker value={color} onChange={setColor} label="Color" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="edit-skill-category"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Category
                </label>
                <input
                  id="edit-skill-category"
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. Research, Writing"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>

              <div>
                <label htmlFor="edit-skill-tags" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Tags
                </label>
                <input
                  id="edit-skill-tags"
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                />
                <p className="text-xs text-content-quaternary mt-1">Comma-separated</p>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
